/**
 * Ticket Workflow Stepper
 *
 * Visual indicator for ticket status workflow:
 * Opening → Work In Progress → Completed → Closed
 */

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Check,
    Circle,
    Clock,
    Lock,
    PlayCircle,
    HelpCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
    TICKET_STATUS_ESCALATED,
    TICKET_STATUS_WORK_IN_PROGRESS,
} from '@/modules/Ticket/constants'

type TicketStatusStep = 'opening' | 'work-in-progress' | 'completed' | 'closed'

interface WorkflowStepperProps {
    currentStatus: string
    className?: string
    /** Service tickets end at "completed", no "closed" step */
    isServiceTicket?: boolean
}

interface StepConfig {
    key: TicketStatusStep
    labelKey: string
    icon: React.ReactNode
    hint: string
}

const STEPS: StepConfig[] = [
    {
        key: 'opening',
        labelKey: 'ticket-status-opening',
        icon: <Circle className="w-4 h-4" />,
        hint: 'Ticket mới được tạo, chờ tiếp nhận xử lý',
    },
    {
        key: 'work-in-progress',
        labelKey: 'ticket-status-work-in-progress',
        icon: <PlayCircle className="w-4 h-4" />,
        hint: 'Ticket đang được xử lý bởi nhân viên kỹ thuật',
    },
    {
        key: 'completed',
        labelKey: 'ticket-status-completed',
        icon: <Clock className="w-4 h-4" />,
        hint: 'Ticket đã xử lý xong. Service ticket: hoàn tất tại đây. Ticket thường: chờ Manager đóng',
    },
    {
        key: 'closed',
        labelKey: 'ticket-status-closed',
        icon: <Lock className="w-4 h-4" />,
        hint: 'Ticket đã đóng hoàn toàn (chỉ áp dụng cho ticket thường, không áp dụng cho service ticket)',
    },
]

const WorkflowStepper: React.FC<WorkflowStepperProps> = ({
    currentStatus,
    className,
    isServiceTicket = false,
}) => {
    const { t } = useTranslation()
    // Service tickets don't have "closed" step
    const steps = isServiceTicket
        ? STEPS.filter((step) => step.key !== 'closed')
        : STEPS
    // 'escalated' is a flag raised on a ticket that is still being worked, not a
    // stage of its own. Left unmapped it misses every step and the fallback
    // below lands on 0, drawing a mid-flight ticket as if it were back at the
    // first step.
    const stageKey =
        currentStatus === TICKET_STATUS_ESCALATED
            ? TICKET_STATUS_WORK_IN_PROGRESS
            : currentStatus
    const currentIndex = steps.findIndex((step) => step.key === stageKey)
    const safeCurrentIndex = currentIndex >= 0 ? currentIndex : 0
    const [hoveredStep, setHoveredStep] = useState<number | null>(null)

    return (
        <div className={cn('flex flex-col gap-2', className)}>
            {/* Help hint - subtle, consistent style */}
            <div className="flex items-center justify-end">
                <span className="text-[10px] text-gray-400 flex items-center gap-1">
                    <HelpCircle className="w-3 h-3" />
                    Hover để xem chi tiết
                </span>
            </div>
            {/* Icons row with connector lines */}
            <div className="flex items-center">
                {steps.map((step, index) => {
                    const isCompleted = index < safeCurrentIndex
                    const isCurrent = index === safeCurrentIndex
                    const isUpcoming = index > safeCurrentIndex

                    return (
                        <React.Fragment key={step.key}>
                            {/* Step Icon with tooltip */}
                            <div
                                className="relative"
                                onMouseEnter={() => setHoveredStep(index)}
                                onMouseLeave={() => setHoveredStep(null)}
                            >
                                <div
                                    className={cn(
                                        'w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors shrink-0 cursor-help',
                                        isCompleted &&
                                            'bg-green-500 border-green-500 text-white',
                                        isCurrent &&
                                            'bg-blue-500 border-blue-500 text-white',
                                        isUpcoming &&
                                            'bg-gray-100 border-gray-300 text-gray-400'
                                    )}
                                >
                                    {isCompleted ? (
                                        <Check className="w-4 h-4" />
                                    ) : (
                                        step.icon
                                    )}
                                </div>
                                {/* Tooltip */}
                                {hoveredStep === index && (
                                    <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 animate-in fade-in zoom-in-95 duration-150">
                                        <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
                                            <p className="font-medium mb-1">
                                                {t(step.labelKey)}
                                            </p>
                                            <p className="text-gray-300 text-[11px] leading-relaxed">
                                                {step.hint}
                                            </p>
                                        </div>
                                        {/* Arrow */}
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                                            <div className="border-4 border-transparent border-t-gray-900" />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Connector line */}
                            {index < steps.length - 1 && (
                                <div
                                    className={cn(
                                        'flex-1 h-0.5 transition-colors',
                                        index < safeCurrentIndex
                                            ? 'bg-green-500'
                                            : 'bg-gray-200'
                                    )}
                                />
                            )}
                        </React.Fragment>
                    )
                })}
            </div>

            {/* Labels row */}
            <div className="flex justify-between">
                {steps.map((step, index) => {
                    const isCompleted = index < safeCurrentIndex
                    const isCurrent = index === safeCurrentIndex
                    const isUpcoming = index > safeCurrentIndex

                    return (
                        <span
                            key={step.key}
                            className={cn(
                                'text-xs font-medium text-center whitespace-nowrap',
                                // Distribute labels evenly under each icon
                                index === 0 && 'text-left',
                                index === steps.length - 1 && 'text-right',
                                isCompleted && 'text-green-600',
                                isCurrent && 'text-blue-600',
                                isUpcoming && 'text-gray-400'
                            )}
                        >
                            {t(step.labelKey)}
                        </span>
                    )
                })}
            </div>
        </div>
    )
}

export default WorkflowStepper
